import FullScreenLoader from "components/FullScreenLoader";
import AuthLayout from "layout/AuthLayout";
import { Spin, message } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { closePaymentModal } from "flutterwave-react-v3";

import authStore from "mobx/AuthStore";

import paystackIcon from "assets/images/paystack.png";
import flutterWaveIcon from "assets/images/flutterwave.png";

import { CreditCardImg } from "utils/assets";

import { BASE_URL } from "services";
import http from "services/httpService";
import { verifyPayment } from "services/LoanService";
import { usePayment } from "../../../config";

const PaymentMethodButton = ({
  isActive = false,
  imageURL,
  onClick,
  label,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full sm:w-[150px] rounded-lg border p-4 h-14 transition ${
        isActive
          ? "border-2 border-primary shadow-lg bg-white"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      {isActive && (
        <span className="inline-flex items-center justify-center h-6 w-6 absolute -top-2 -left-2 rounded-full bg-primary text-white">
          <i className="bi bi-check text-sm" />
        </span>
      )}

      <img
        src={imageURL}
        className="object-contain w-full h-full"
        alt={label}
      />
    </button>
  );
};

function ProfilePreviewItem({ label, value }) {
  return (
    <div className="bg-white rounded-lg border border-gray-100 p-3">
      <p className="text-[11px] text-gray-400">{label}</p>

      <p
        className="text-xs font-medium text-gray-700 mt-1 truncate"
        title={value || ""}
      >
        {value || "Not provided"}
      </p>
    </div>
  );
}

function PaymentSection({ title, children }) {
  return (
    <section>
      <div className="mb-3">
        <h2 className="text-base font-semibold text-gray-800">{title}</h2>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6">
        {children}
      </div>
    </section>
  );
}

const ReapplyPaymentPage = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loan, setLoan] = useState(null);
  const [loadingLoan, setLoadingLoan] = useState(true);
  const [loanError, setLoanError] = useState(null);

  const [userProfile, setUserProfile] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);

  /*
   * Fetch loan
   */
  useEffect(() => {
    if (!id) {
      setLoanError(new Error("Loan ID is missing."));
      setLoadingLoan(false);
      return;
    }

    let mounted = true;

    const fetchLoan = async () => {
      setLoadingLoan(true);
      setLoanError(null);

      try {
        const res = await http.get(`${BASE_URL}/loans/${id}`);

        /*
         * API response:
         *
         * {
         *   data: {
         *     loan: [
         *       {...}
         *     ]
         *   }
         * }
         *
         * Therefore the actual loan is:
         *
         * res.data.data.loan[0]
         */
        const loans = res?.data?.data?.loan;

        const fetchedLoan = Array.isArray(loans) ? loans[0] : loans;

        if (!fetchedLoan) {
          throw new Error("Loan application could not be found.");
        }

        if (mounted) {
          setLoan(fetchedLoan);
        }
      } catch (error) {
        if (mounted) {
          setLoanError(error);

          message.error(
            error?.response?.data?.message ||
              error?.message ||
              "Unable to retrieve your loan application.",
          );
        }
      } finally {
        if (mounted) {
          setLoadingLoan(false);
        }
      }
    };

    fetchLoan();

    return () => {
      mounted = false;
    };
  }, [id]);

  /*
   * Fetch logged-in user
   */
  useEffect(() => {
    let mounted = true;

    const fetchUserProfile = async () => {
      setLoadingUser(true);

      try {
        authStore.loggedInStatus();

        const res = await http.get(`${BASE_URL}/users/who_i_am`);

        if (mounted) {
          setUserProfile(res?.data?.data);
        }
      } catch (error) {
        message.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to retrieve your profile.",
        );
      } finally {
        if (mounted) {
          setLoadingUser(false);
        }
      }
    };

    fetchUserProfile();

    return () => {
      mounted = false;
    };
  }, []);

  /*
   * Processing fee
   */
  const processingFee = Number(
    loan?.processing_fee ??
      loan?.processing_fee_amount ??
      loan?.application_fee ??
      2000,
  );

  const formatMoney = (value) => {
    if (!value) return "₦0";

    return `₦${Number(value).toLocaleString("en-NG", {
      maximumFractionDigits: 2,
    })}`;
  };

  /*
   * Existing payment implementation
   */
  const { loading: paymentHookLoading, startPayment } = usePayment({
    paymentMethod,
    userProfile,

    callback: async (response) => {
      try {
        setPaymentLoading(true);

        if (
          response.status !== "successful" &&
          response.status !== "success" &&
          response.status !== "completed"
        ) {
          message.error("Payment was not completed.");
          return;
        }

        const { tx_ref: reference, transaction_id } = response;

        let payment;

        if (paymentMethod !== "paystack") {
          payment = await verifyPayment({
            reference,
            id,
            transaction_id,
          });
        } else {
          payment = await verifyPayment({
            paymentMethod,
            ...response,
            id,
          });
        }

        if (!payment) {
          throw new Error("Unable to verify your payment.");
        }

        closePaymentModal();

        message.success("Payment completed successfully.");

        navigate("/dashboard", {
          replace: true,
        });
      } catch (error) {
        message.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to verify your payment.",
        );
      } finally {
        setPaymentLoading(false);
      }
    },
  });

  const loading = paymentLoading || paymentHookLoading;

  const handlePayment = () => {
    if (!paymentMethod) {
      message.error("Please select a payment method.");
      return;
    }

    if (!processingFee || processingFee <= 0) {
      message.error("The processing fee could not be determined.");
      return;
    }

    startPayment();
  };

  /*
   * Loading
   */
  if (loadingLoan || loadingUser) {
    return <FullScreenLoader show={true} />;
  }

  /*
   * Loan not found
   */
  if (loanError || !loan) {
    return (
      <AuthLayout>
        <div className="max-w-xl mx-auto py-16 px-4 font-poppins">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
              <i className="bi bi-file-earmark-x text-2xl text-gray-500" />
            </div>

            <h2 className="mt-4 text-lg font-semibold text-gray-800">
              Loan Application Not Found
            </h2>

            <p className="mt-2 text-sm text-gray-500">
              We could not find the loan application associated with this
              payment.
            </p>

            <button
              type="button"
              onClick={() => navigate("/dashboard/reapply")}
              className="mt-6 px-6 py-2.5 rounded-lg bg-primary-800 text-white text-sm font-medium"
            >
              Back to Applications
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const institution = loan.institution || {};

  return (
    <AuthLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 pb-24 font-poppins">
        {/* HEADER */}
        <header className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <span>Loan Application</span>

            <i className="bi bi-chevron-right text-xs" />

            <span className="text-primary-700 font-medium">Payment</span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            Complete Your Payment
          </h1>

          <p className="text-sm text-gray-500 mt-2 max-w-2xl">
            Your loan application is ready. Complete the processing fee payment
            below to continue with your application.
          </p>
        </header>

        <div className="space-y-6">
          {/* APPLICATION SUMMARY */}
          <section>
            <div className="mb-3">
              <h2 className="text-base font-semibold text-gray-800">
                Application Summary
              </h2>

              <p className="text-xs text-gray-500 mt-1">
                Details of the loan application you are paying for.
              </p>
            </div>

            <div className="border border-gray-200 rounded-xl bg-gray-50 p-4 sm:p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <ProfilePreviewItem
                  label="Loan Amount"
                  value={formatMoney(loan.amount)}
                />

                <ProfilePreviewItem
                  label="Loan Tenure"
                  value={
                    loan.loan_tenure ? `${loan.loan_tenure} months` : undefined
                  }
                />

                <ProfilePreviewItem
                  label="Institution"
                  value={institution.name}
                />

                <ProfilePreviewItem label="Course" value={institution.course} />
              </div>
            </div>
          </section>

          {/* PROCESSING FEE */}
          <PaymentSection title="Processing Fee">
            <div className="rounded-xl bg-primary-50 border border-primary-100 p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                  <i className="bi bi-credit-card text-lg" />
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Loan Processing Fee
                  </p>

                  <p className="text-xs text-gray-500 mt-1">
                    This fee is required before your loan application can
                    proceed to processing.
                  </p>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-primary-100">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-gray-500">Amount to pay</span>

                  <span className="text-2xl font-semibold text-primary-800">
                    {formatMoney(processingFee)}
                  </span>
                </div>
              </div>
            </div>
          </PaymentSection>

          {/* PAYMENT METHOD */}
          <PaymentSection title="Payment Method">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Pick a Payment Method
              </p>

              <p className="text-xs text-gray-500 mt-1">
                Select how you would like to pay your processing fee.
              </p>

              <div className="mt-5 flex flex-col sm:flex-row gap-4">
                <PaymentMethodButton
                  label="Paystack"
                  imageURL={paystackIcon}
                  isActive={paymentMethod === "paystack"}
                  onClick={() => setPaymentMethod("paystack")}
                />

                <PaymentMethodButton
                  label="Flutterwave"
                  imageURL={flutterWaveIcon}
                  isActive={paymentMethod === "flutterwave"}
                  onClick={() => setPaymentMethod("flutterwave")}
                />
              </div>
            </div>
          </PaymentSection>

          {/* PAYMENT INFORMATION */}
          <section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-start gap-3">
              <i className="bi bi-shield-check text-primary-700 mt-0.5" />

              <div>
                <p className="text-sm font-medium text-gray-700">
                  Secure Payment
                </p>

                <p className="text-xs text-gray-500 mt-1 leading-5">
                  Your payment will be securely processed through your selected
                  payment provider. Your application will continue once the
                  payment has been successfully verified.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* FOOTER */}
        <footer className="mt-8 pt-5 border-t border-gray-200 flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={loading}
            onClick={() =>
              navigate(
                `/dashboard/apply/${loan.loan_type || "undergraduate"}/${id}`,
              )
            }
            className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Back to Application
          </button>

          <button
            type="button"
            disabled={loading || !paymentMethod || processingFee <= 0}
            onClick={handlePayment}
            className="px-7 py-2.5 rounded-lg bg-primary-800 text-white text-sm font-medium hover:bg-primary-900 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <Spin size="small" />
                Processing...
              </>
            ) : (
              <>
                Pay {formatMoney(processingFee)}
                <i className="bi bi-arrow-right" />
              </>
            )}
          </button>
        </footer>
      </div>
    </AuthLayout>
  );
});

export default ReapplyPaymentPage;
